import { useCallback, useEffect, useState } from "react";
import {
  dashboardRequest,
  DashboardRequestError,
} from "../providers/aws/client";

// Payload of the dashboard-api GET /pipeline/render-queue endpoint
// (echoflicks-infra services/dashboard-api/render_queue_api.py, plan
// 2026-09-26 H3Q-09): the Echoes the render admission gate holds before the
// composer, read from the gate's own authority table rather than the
// pipeline-monitor projection. Admin-only through the /api/dashboard proxy.

export type RenderQueueForecast = {
  local_start_at: string | null;
  finish_if_wait_at: string | null;
  finish_if_admit_now_at: string | null;
  slack_seconds: number | null;
  deadline_at_risk: boolean;
};

export type RenderQueueRow = {
  workload_id: string;
  generation: string;
  state: "READY" | "ADMITTED" | "NEEDS_OPERATOR" | string;
  mode: "off" | "shadow" | "enforce" | string | null;
  governed: boolean;
  request_status: string | null;
  product_flow: string | null;
  tier: string | null;
  priority: number | null;
  h3_clips: number | null;
  ready_at: string | null;
  admitted_at: string | null;
  terminal_at: string | null;
  queue_wait_seconds: number | null;
  admission_reason: string | null;
  decision_reason: string | null;
  /** Zero-based and approximate: the gate re-ranks on every poll. Null once admitted. */
  approximate_position: number | null;
  ahead: number | null;
  local_capacity: number | null;
  /** Owned GPUs the gate could not count, with the exact reasons. */
  not_schedulable: Record<string, string[]>;
  promise_basis: string;
  promise_deadline_at: string | null;
  max_queue_wait_seconds: number | null;
  forecast: RenderQueueForecast | null;
  shadow_reason: string | null;
  cancel_requested: boolean;
  attention_reason: string | null;
  outcome: string | null;
  config_release_id: string | null;
  last_poll_at: string | null;
  /** The waiting execution stopped polling: its position is not current. */
  stale: boolean;
};

export type RenderQueueView = {
  environment: "stg" | "staging";
  as_of: string;
  source: string;
  summary: {
    waiting: number;
    admitted: number;
    needs_operator: number;
    oldest_wait_seconds: number;
    deadline_at_risk: number;
    stale_waiters: number;
  };
  waiting: RenderQueueRow[];
  admitted: RenderQueueRow[];
  needs_operator: RenderQueueRow[];
  recent: RenderQueueRow[];
};

const REASONS: Record<string, string> = {
  local_capacity_free: "an owned GPU was free",
  local_busy: "owned GPUs busy; waiting still meets the promise",
  deadline_spill: "waiting would miss the promise, so it went to cloud",
  max_wait_elapsed: "waited the longest this tier may wait",
  no_local_capacity: "no owned GPU is schedulable",
  admitted_cap: "too many Echoes rendering at once",
  not_governed: "not held by the queue",
  no_promise: "no delivery promise to schedule against",
  shadow_admit: "shadow mode: admitted at once, decision only recorded",
  operator_attention_admit: "admitted after it was flagged",
  poll_guard: "admitted after too many polls",
  admission_contention: "another decision won the race; retrying",
  waiter_not_polling: "the waiting execution stopped polling",
  duplicate_generation: "a second execution for the same render",
  cancelled: "cancelled",
  request_missing: "no queue request was written for this Echo",
  request_invalid: "the queue request could not be read",
  fail_open: "the gate failed open",
};

export function describeReason(reason: string | null | undefined): string {
  if (!reason) return "—";
  return REASONS[reason] ?? reason.replaceAll("_", " ");
}

const WORKER_REASONS: Record<string, string> = {
  stale: "no heartbeat",
  offline: "stopped",
  starting: "starting",
  draining: "paused for maintenance",
  blocked_gpu_busy: "GPU in use by another process",
  blocked_cotenant: "yielded to the Qwen image worker",
  quarantined: "quarantined after failures",
  digest_unverified: "image digest unknown",
  digest_not_accepted: "image digest not in the signed allowlist",
  location_unverified: "location unknown",
  location_not_allowed: "location not in the signed allowlist",
  dedicated_queue_missing: "dedicated queue missing",
  queue_unverified: "dedicated queue not verified",
  consecutive_failures: "3+ consecutive failures",
};

export function describeWorkerReason(reason: string): string {
  if (reason.startsWith("state_unknown:"))
    return `unknown state ${reason.slice("state_unknown:".length)}`;
  return WORKER_REASONS[reason] ?? reason.replaceAll("_", " ");
}

export function environmentLabel(environment: string): string {
  if (environment === "stg") return "Production (stg)";
  if (environment === "staging") return "Engineering staging (staging)";
  return environment;
}

/** "1 h 5 min", "12 min", "40 s". */
export function waitLabel(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

type RenderQueueState = {
  data?: RenderQueueView;
  error?: Error;
  /** The API answered that this environment has no render queue (route or table absent). */
  unavailable?: string;
  loading: boolean;
};

/**
 * Polls /pipeline/render-queue while the tab is visible. Component-local like
 * the rest of the Queue page: protected rows never enter the persisted query
 * cache. A failed poll keeps the last rows on screen, marked as not current.
 */
export function useRenderQueue(refreshSeconds: number) {
  const [state, setState] = useState<RenderQueueState>({ loading: true });
  const [refresh, setRefresh] = useState(0);
  const reload = useCallback(() => setRefresh((value) => value + 1), []);

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const interval = Math.max(5, refreshSeconds) * 1000;
    const fetchOnce = async () => {
      if (disposed || document.hidden) return;
      controller = new AbortController();
      setState((previous) => ({ ...previous, loading: true }));
      try {
        const data = await dashboardRequest<RenderQueueView>(
          "/pipeline/render-queue",
          { signal: controller.signal, cache: "no-store" },
        );
        if (disposed) return;
        if (
          !data ||
          !["stg", "staging"].includes(data.environment) ||
          !Array.isArray(data.waiting)
        )
          throw new Error("Invalid render queue response");
        setState({ data, loading: false });
      } catch (error) {
        if (disposed || controller.signal.aborted) return;
        const status =
          error instanceof DashboardRequestError ? error.status : null;
        if (status === 404 || status === 503) {
          setState({
            loading: false,
            unavailable:
              status === 404
                ? "This dashboard API predates the render queue."
                : "The render queue table is not reachable in this environment.",
          });
        } else {
          setState((previous) => ({
            data: previous.data,
            loading: false,
            error:
              error instanceof Error
                ? error
                : new Error("Render queue unavailable"),
          }));
        }
        if (status === 401 || status === 403) return;
      }
      if (!disposed) timer = setTimeout(fetchOnce, interval);
    };
    const visibilityChanged = () => {
      clearTimeout(timer);
      if (document.hidden) controller?.abort();
      else void fetchOnce();
    };
    document.addEventListener("visibilitychange", visibilityChanged);
    void fetchOnce();
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, [refreshSeconds, refresh]);

  return { ...state, reload };
}
